/**
 * Browser-side `compare_structures`: read-only, deterministic comparison of
 * two target structures (GTV / CTV / PTV) in the active structure set —
 * normally the current one and its "<name>_prior" copy. Every number comes
 * from the viewer's own calculations: volumes (`getRoiVolumeCm3`), centre
 * of mass and extent (`getRoiStatistics`), and overlap / new growth /
 * regression from its boolean operation on the two structures (AND,
 * AND-NOT) measured without creating a structure. Units converted here:
 * mm → cm. Directions are DICOM patient (LPS): +X left, +Y posterior,
 * +Z superior. Reliability warnings are always returned. Ports injected.
 */
import { resolveRoi } from '../core/roiResolver';
import { asRecord, toListed, type StructureFacts } from '../core';
import type { CompareStructuresOutput } from '../../contract';
import type { RoiStats } from '../structureProperties/structureProperties';
import { isTargetType, TARGET_TYPES } from './timepoints';

export interface CompareDeps {
    readonly structures: ReadonlyArray<StructureFacts>;
    /** Type and generation algorithm (MANUAL / AUTOMATIC / …). */
    roiInfo(
        roiNumber: number
    ): { readonly type: string; readonly algorithm: string } | undefined;
    volumeCm3(roiNumber: number): number;
    stats(roiNumber: number): RoiStats | undefined;
    /** Volume (cm³) of A∩B ('and') or A minus B ('and-not'). */
    booleanVolumeCm3(a: number, b: number, op: 'and' | 'and-not'): number;
    /** Slice spacing of the current scan (mm). */
    sliceSpacingMm(): number | undefined;
}

/** Below this a centre shift along an axis is reported as none (cm). */
const SHIFT_THRESHOLD_CM = 0.1;
const SMALL_VOLUME_CM3 = 1;

const r = (v: number, dp: number) => Number(v.toFixed(dp));
const ref = (c: { roiNumber: number; name: string }) => ({
    roiNumber: c.roiNumber,
    name: c.name,
});
const diameter = (v: number) => 2 * Math.cbrt((3 * v) / (4 * Math.PI));

/** "0.6 cm superior, 0.2 cm left" — components of ≥ 0.1 cm. */
export function describeShift(
    dxCm: number,
    dyCm: number,
    dzCm: number
): string {
    const parts: string[] = [];
    const add = (d: number, pos: string, neg: string) => {
        if (Math.abs(d) >= SHIFT_THRESHOLD_CM) {
            parts.push(`${Math.abs(d).toFixed(1)} cm ${d > 0 ? pos : neg}`);
        }
    };
    add(dzCm, 'superior', 'inferior');
    add(dyCm, 'posterior', 'anterior');
    add(dxCm, "toward the patient's left", "toward the patient's right");
    return parts.length > 0 ? parts.join(', ') : 'no shift (under 0.1 cm)';
}

export function compareStructures(
    input: unknown,
    deps: CompareDeps
): CompareStructuresOutput {
    const o = asRecord(input);
    const listed = deps.structures.map(toListed);
    const pick = (name: unknown) => {
        if (typeof name !== 'string' || !name) {
            return { kind: 'missing' as const };
        }
        return resolveRoi(name, listed);
    };
    const a = pick(o?.current);
    const b = pick(o?.prior);
    for (const res of [a, b]) {
        if (res.kind === 'missing') {
            return { status: 'unavailable', reason: 'name both structures' };
        }
        if (res.kind === 'ambiguous') {
            return {
                status: 'ambiguous',
                reason: res.reason,
                candidates: res.candidates.map(ref),
            };
        }
        if (res.kind === 'not-found') {
            return {
                status: 'not-found',
                suggestions: res.suggestions.map(ref),
            };
        }
    }
    if (a.kind !== 'resolved' || b.kind !== 'resolved') {
        return { status: 'unavailable', reason: 'name both structures' };
    }
    const cur = a.structure;
    const pri = b.structure;
    if (cur.roiNumber === pri.roiNumber) {
        return {
            status: 'unavailable',
            reason: 'choose two different structures',
        };
    }
    const curInfo = deps.roiInfo(cur.roiNumber);
    const priInfo = deps.roiInfo(pri.roiNumber);
    for (const [name, info] of [
        [cur.name, curInfo],
        [pri.name, priInfo],
    ] as const) {
        if (!info || !isTargetType(info.type)) {
            return {
                status: 'unavailable',
                reason:
                    `'${name}' is not a target structure; only ` +
                    `${TARGET_TYPES.join(', ')} can be compared`,
            };
        }
    }
    const vCur = deps.volumeCm3(cur.roiNumber);
    const vPri = deps.volumeCm3(pri.roiNumber);
    const sCur = deps.stats(cur.roiNumber);
    const sPri = deps.stats(pri.roiNumber);
    if (
        !(vCur > 0) ||
        !(vPri > 0) ||
        !sCur?.centerOfMassMm ||
        !sPri?.centerOfMassMm ||
        !sCur.boundingBox ||
        !sPri.boundingBox
    ) {
        return {
            status: 'unavailable',
            reason: 'both structures need contours on this scan',
        };
    }
    let overlap: number;
    let growth: number;
    let regression: number;
    try {
        overlap = deps.booleanVolumeCm3(cur.roiNumber, pri.roiNumber, 'and');
        growth = deps.booleanVolumeCm3(cur.roiNumber, pri.roiNumber, 'and-not');
        regression = deps.booleanVolumeCm3(
            pri.roiNumber,
            cur.roiNumber,
            'and-not'
        );
    } catch (err) {
        return {
            status: 'unavailable',
            reason: `the overlap could not be computed (${String(err)})`,
        };
    }
    const d = [0, 1, 2].map(
        (i) => (sCur.centerOfMassMm![i] - sPri.centerOfMassMm![i]) / 10
    );
    const extent = (s: RoiStats) => {
        const bb = s.boundingBox!;
        return {
            x: r((bb.max[0] - bb.min[0]) / 10, 2),
            y: r((bb.max[1] - bb.min[1]) / 10, 2),
            z: r((bb.max[2] - bb.min[2]) / 10, 2),
        };
    };

    const warnings: string[] = [];
    if (pri.name.toLowerCase().endsWith('_prior')) {
        const spacing = deps.sliceSpacingMm();
        warnings.push(
            'The earlier contour was resampled onto this scan through the ' +
                'registration; differences smaller than about one slice' +
                (spacing ? ` (${(spacing / 10).toFixed(2)} cm)` : '') +
                ' are within resampling and registration error.'
        );
    } else {
        warnings.push(
            'These are two structures on the same scan; make sure they come ' +
                'from different visits.'
        );
    }
    if (curInfo!.algorithm !== priInfo!.algorithm) {
        warnings.push(
            `The contours were made differently (${priInfo!.algorithm || 'unknown'} ` +
                `earlier, ${curInfo!.algorithm || 'unknown'} now); part of the ` +
                'difference may come from the contouring method.'
        );
    }
    if (vCur < SMALL_VOLUME_CM3 || vPri < SMALL_VOLUME_CM3) {
        warnings.push(
            'Small volumes: a few voxels change the percentages noticeably.'
        );
    }

    return {
        status: 'ok',
        current: cur.name,
        prior: pri.name,
        volumeCm3: { current: r(vCur, 2), prior: r(vPri, 2) },
        volumeChangeCm3: r(vCur - vPri, 2),
        volumeChangePercent: r(((vCur - vPri) / vPri) * 100, 1),
        equivalentDiameterCm: {
            current: r(diameter(vCur), 2),
            prior: r(diameter(vPri), 2),
        },
        newGrowthCm3: r(growth, 2),
        regressionCm3: r(regression, 2),
        overlapCm3: r(overlap, 2),
        dice: r((2 * overlap) / (vCur + vPri), 3),
        centreShiftCm: { x: r(d[0], 2), y: r(d[1], 2), z: r(d[2], 2) },
        centreShiftDistanceCm: r(Math.hypot(d[0], d[1], d[2]), 2),
        centreShiftDirection: describeShift(d[0], d[1], d[2]),
        extentCm: { current: extent(sCur), prior: extent(sPri) },
        warnings,
    };
}
